import React, { useEffect, useState } from 'react';
import { listPaymentMethods } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { paymentMethodExpiry, paymentMethodLabel } from '@/format';
import NewCardElement from './NewCardElement';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';

/**
 * The "Payment method" block: the default-card chip or a
 * dashed "Add a card" launcher when there's none; expanding reveals the
 * saved-method radio list + an embedded Stripe PaymentElement for a new card.
 *
 * Reports the parent's choice upward via onSelectionChange:
 *   {type:'saved', pmId} · {type:'new'}
 * The current default is preselected BY ID (the status endpoint's chip
 * carries it), and once the list has loaded the section always reports a
 * concrete choice — there is no "leave it as it is" state a Save could act
 * on without a request (PDL002 BE-02). The new-card confirm function
 * arrives via onBridge (see NewCardElement).
 */
const PaymentMethodSection = ({
	studentId,
	stripeAccountId,
	defaultPm,
	onSelectionChange,
	onBridge,
	forceExpanded,
}) => {
	const [expanded, setExpanded] = useState(forceExpanded || !defaultPm);
	const [methods, setMethods] = useState(null);
	const [error, setError] = useState(null);
	const [selected, setSelected] = useState(defaultPm?.id ?? 'new');
	// The rail a brand-new method will settle on, as the Stripe element
	// reports it while the parent fills it in (card vs US bank account) — the
	// quote above the section prices the service fee by rail.
	const [newMethodType, setNewMethodType] = useState(null);

	const railOf = (pm) => pm?.type ?? (pm?.bank_name ? 'us_bank_account' : null);

	const report = (value, pmType) =>
		onSelectionChange(value === 'new' ? { type: 'new', pmType } : { type: 'saved', pmId: value, pmType });

	useEffect(() => {
		if (forceExpanded) setExpanded(true);
	}, [forceExpanded]);

	// A collapsed default chip IS a concrete choice. Report it before any
	// list loads (the list only loads once expanded), or the parent screen's
	// submit guard sees an empty selection and the first tap on "Turn on
	// Auto-Pay" does nothing until the parent opens Change and re-picks.
	useEffect(() => {
		if (defaultPm) report(defaultPm.id, railOf(defaultPm));
	}, []);

	useEffect(() => {
		if (!expanded || methods) return;
		listPaymentMethods(studentId)
			.then((list) => setMethods(Array.isArray(list) ? list : []))
			.catch((err) => setError(parseHostedError(err).message));
	}, [expanded, methods, studentId]);

	const select = (value) => {
		setSelected(value);
		report(value, value === 'new' ? newMethodType : railOf((methods ?? []).find((m) => m.id === value) ?? defaultPm));
	};

	const handleNewMethodType = (pmType) => {
		setNewMethodType(pmType);
		if (selected === 'new') report('new', pmType);
	};

	// The list is the source of truth for what can be picked: keep the
	// preselected id if it is still there, otherwise fall back to whichever
	// row Stripe holds as default, and report that choice upward so the
	// parent screen never holds a stale or empty selection.
	useEffect(() => {
		if (!methods) return;
		const known = selected === 'new' || methods.some((m) => m.id === selected);
		select(known ? selected : (methods.find((m) => m.is_default)?.id ?? 'new'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [methods]);

	if (!expanded) {
		return (
			<div className="flex w-full flex-col gap-[8px]">
				<p className="text-[13px] font-semibold text-pay-ink">Payment method</p>
				{defaultPm ? (
					<div className="flex h-[56px] w-full items-center justify-between rounded-[12px] border-[1.5px] border-pay-line bg-white px-[16px]">
						<p className="flex items-center gap-[10px] text-[15px] font-semibold text-pay-ink">
							{paymentMethodLabel(defaultPm)}
							{paymentMethodExpiry(defaultPm) ? (
								<span className="text-[13px] font-normal text-pay-muted">{paymentMethodExpiry(defaultPm)}</span>
							) : null}
						</p>
						<button
							type="button"
							onClick={() => setExpanded(true)}
							className="text-[13px] font-semibold text-pd-blue-new"
						>
							Change
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => {
							setExpanded(true);
							select('new');
						}}
						className="flex h-[56px] w-full items-center justify-between rounded-[12px] border-[1.5px] border-dashed border-pd-blue-new bg-white px-[16px]"
					>
						<span className="flex items-center gap-[8px] text-pd-blue-new">
							<span className="text-[18px] font-bold">＋</span>
							<span className="text-[15px] font-semibold">Add a payment method</span>
						</span>
						<span className="text-[12px] font-medium text-pay-muted">🔒 via Stripe</span>
					</button>
				)}
				<p className="text-[12px] text-pay-muted">You’ll enter your payment details securely on Stripe.</p>
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col gap-[8px]">
			<p className="text-[13px] font-semibold text-pay-ink">Payment method</p>

			{error ? <ErrorBanner>{error}</ErrorBanner> : null}
			{!methods && !error ? <Spinner label="Loading payment methods…" /> : null}

			{(methods ?? []).map((pm) => (
				<label
					key={pm.id}
					className={`flex h-[52px] w-full cursor-pointer items-center gap-[12px] rounded-[12px] border-[1.5px] bg-white px-[16px] ${
						selected === pm.id ? 'border-pay-purple' : 'border-pay-line'
					}`}
				>
					<input
						type="radio"
						name="pay-pm"
						checked={selected === pm.id}
						onChange={() => select(pm.id)}
						className="h-[16px] w-[16px] accent-pay-purple"
					/>
					<span className="flex-1 text-[15px] font-semibold text-pay-ink">{paymentMethodLabel(pm)}</span>
					{paymentMethodExpiry(pm) ? (
						<span className="text-[13px] text-pay-muted">{paymentMethodExpiry(pm)}</span>
					) : null}
					{pm.is_default ? <span className="text-[12px] font-medium text-pd-blue-new">Default</span> : null}
				</label>
			))}

			<label
				className={`flex h-[52px] w-full cursor-pointer items-center gap-[12px] rounded-[12px] border-[1.5px] bg-white px-[16px] ${
					selected === 'new' ? 'border-pay-purple' : 'border-dashed border-pd-blue-new'
				}`}
			>
				<input
					type="radio"
					name="pay-pm"
					checked={selected === 'new'}
					onChange={() => select('new')}
					className="h-[16px] w-[16px] accent-pay-purple"
				/>
				<span className="flex-1 text-[15px] font-semibold text-pd-blue-new">＋ Add a new payment method</span>
				<span className="text-[12px] font-medium text-pay-muted">🔒 via Stripe</span>
			</label>

			{selected === 'new' ? (
				<div className="pt-[4px]">
					<NewCardElement
						studentId={studentId}
						stripeAccountId={stripeAccountId}
						onBridge={onBridge}
						onTypeChange={handleNewMethodType}
					/>
				</div>
			) : null}
		</div>
	);
};

export default PaymentMethodSection;
